import { Button, CircularProgress, Text } from '@shared/components';
import {
  downloadFile,
  makeElementClassNameFactory,
  makeRootClassName,
} from '@shared/utils';
import Papa from 'papaparse';
import { useEffect, useState } from 'react';
import { DocViewerMessage } from '../doc-viewer/DocViewerMessage';

const ROOT = makeRootClassName('CsvViewer');
const el = makeElementClassNameFactory(ROOT);

type CsvViewerProps = {
  fileName?: string;
  src: string;
  title: string;
};

function pluralize(count: number, noun: string): string {
  return `${count.toLocaleString()} ${noun}${count === 1 ? '' : 's'}`;
}

function CsvViewer({ fileName, src, title }: CsvViewerProps) {
  const [rows, setRows] = useState<string[][]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const loadCsv = async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        const response = await fetch(src);
        if (!response.ok) {
          throw new Error(`Failed to fetch CSV: ${response.status}`);
        }
        const { data } = Papa.parse<string[]>(await response.text(), {
          skipEmptyLines: true,
        });
        setRows(data);
      } finally {
        setIsLoading(false);
      }
    };

    loadCsv().catch((err) => {
      console.error('Failed to load CSV', err);
      setHasError(true);
    });
  }, [src]);

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center py-12">
        <CircularProgress size="small" />
      </div>
    );
  }

  if (hasError) {
    return (
      <DocViewerMessage
        message="This CSV could not be loaded."
        action={
          <Button
            variant="secondary"
            onPress={() => downloadFile(src, fileName).catch(console.error)}
          >
            Download CSV
          </Button>
        }
      />
    );
  }

  const [headerRow, ...bodyRows] = rows;
  if (!headerRow) {
    return <DocViewerMessage message="This CSV is empty." />;
  }

  return (
    <div className={el`csv-viewer`}>
      <div className={el`table-scroll`}>
        <table className={el`csv-table`}>
          <caption className="sr-only">{title}</caption>
          <thead>
            <tr>
              {headerRow.map((header, index) => (
                <th key={index} scope="col">
                  <span className={el`cell-text`} title={header}>
                    {header}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex}>
                    <span className={el`cell-text`} title={cell}>
                      {cell}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Text className={el`summary`} type="body-xs">
        {`${pluralize(bodyRows.length, 'row')} × ${pluralize(
          headerRow.length,
          'column'
        )}`}
      </Text>
    </div>
  );
}

export default CsvViewer;
